import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Shield, ArrowRight, Check, Loader2 } from "lucide-react";

// Parent-facing catalogue of vetted pet-insurance partners.
// Clicking "Get a quote" logs a referral and opens the partner site in a new tab.
export default function Insurance() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [pets, setPets] = useState([]);
  const [selectedPet, setSelectedPet] = useState({}); // partner_id → pet_id
  const [loading, setLoading] = useState(true);
  const [clicking, setClicking] = useState({});

  useEffect(() => {
    axios.get(`${API}/insurance-partners`)
      .then(r => setPartners(r.data?.items || []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
    if (user) {
      axios.get(`${API}/pets`, { withCredentials: true })
        .then(r => setPets(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
        .catch(() => setPets([]));
    }
  }, [user]);

  async function getQuote(partner) {
    if (!user) { toast.error("Please sign in to request a quote"); return; }
    setClicking(prev => ({ ...prev, [partner.partner_id]: true }));
    try {
      const petId = selectedPet[partner.partner_id] || pets[0]?.pet_id || "";
      const r = await axios.post(`${API}/insurance-referrals/click`,
        { partner_id: partner.partner_id, pet_id: petId },
        { withCredentials: true });
      if (r.data?.url) {
        window.open(r.data.url, "_blank", "noopener,noreferrer");
        toast.success(`Opening ${partner.name} in a new tab…`);
      } else {
        toast.error("This partner has no active link. Please try another.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open partner");
    } finally {
      setClicking(prev => ({ ...prev, [partner.partner_id]: false }));
    }
  }

  return (
    <div className="space-y-6" data-testid="insurance-page">
      <header className="rounded-3xl bg-gradient-to-br from-ink to-[#1a1a1a] text-white p-6 md:p-8">
        <div className="flex items-center gap-3 text-brand text-xs font-bold tracking-widest uppercase">
          <Shield className="w-4 h-4"/>Pet insurance
        </div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold mt-1">
          Vetted partners. Compare and buy in minutes.
        </h1>
        <p className="opacity-80 text-sm mt-2 max-w-2xl">
          We&apos;ve curated insurance partners that specifically cover pets. Pick one, click &quot;Get a quote&quot;
          and we&apos;ll send you to their site with a PetBond referral — no extra cost to you.
        </p>
      </header>

      {loading && (
        <div className="bg-surface border border-line rounded-3xl p-10 text-center text-ink-soft" data-testid="insurance-loading">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading partners…
        </div>
      )}

      {!loading && partners.length === 0 && (
        <div className="bg-surface border border-line rounded-3xl p-10 text-center" data-testid="insurance-empty">
          <Shield className="w-10 h-10 mx-auto text-ink-soft opacity-40 mb-3"/>
          <div className="font-bold">Partners coming soon</div>
          <div className="text-sm text-ink-soft mt-1">We&apos;re onboarding trusted pet-insurance providers. Check back shortly.</div>
        </div>
      )}

      <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {partners.map(p => (
          <div
            key={p.partner_id}
            data-testid={`insurance-partner-${p.partner_id}`}
            className="bg-surface border border-line rounded-3xl p-5 shadow-soft flex flex-col"
          >
            <div className="flex items-center gap-3">
              {p.logo_url ? (
                <img src={p.logo_url} alt={p.name} className="w-12 h-12 rounded-xl object-cover border border-line"/>
              ) : (
                <div className="w-12 h-12 rounded-xl bg-brand/10 grid place-items-center text-brand"><Shield className="w-6 h-6"/></div>
              )}
              <div className="min-w-0">
                <div className="font-heading text-lg font-extrabold truncate">{p.name}</div>
                <div className="text-[11px] text-ink-soft">From ₹{p.monthly_premium_from?.toLocaleString?.() || p.monthly_premium_from}/month</div>
              </div>
            </div>

            {p.plan_summary && (
              <p className="text-sm text-ink-soft mt-3 line-clamp-3">{p.plan_summary}</p>
            )}

            <div className="mt-3 flex flex-wrap gap-1.5">
              <span className="text-[10px] font-bold bg-brand/10 text-brand rounded-full px-2 py-0.5">
                Coverage up to ₹{(p.coverage_max || 0).toLocaleString()}
              </span>
            </div>

            {Array.isArray(p.features) && p.features.length > 0 && (
              <ul className="mt-3 space-y-1">
                {p.features.slice(0, 4).map((f, i) => (
                  <li key={i} className="text-xs flex items-start gap-1.5">
                    <Check className="w-3.5 h-3.5 text-brand-green mt-0.5 shrink-0"/><span>{f}</span>
                  </li>
                ))}
              </ul>
            )}

            {pets.length > 1 && (
              <select
                data-testid={`insurance-pet-select-${p.partner_id}`}
                value={selectedPet[p.partner_id] || pets[0]?.pet_id || ""}
                onChange={e => setSelectedPet(prev => ({ ...prev, [p.partner_id]: e.target.value }))}
                className="mt-4 w-full rounded-full border border-line bg-surface-muted px-3 py-2 text-xs font-bold text-ink"
              >
                {pets.map(pet => (
                  <option key={pet.pet_id} value={pet.pet_id}>{pet.name}</option>
                ))}
              </select>
            )}

            <button
              type="button"
              data-testid={`insurance-cta-${p.partner_id}`}
              onClick={() => getQuote(p)}
              disabled={clicking[p.partner_id]}
              className="mt-4 rounded-full px-4 py-2.5 text-sm font-bold inline-flex items-center justify-center gap-2 bg-ink text-white hover:bg-brand transition-colors disabled:opacity-60"
            >
              {clicking[p.partner_id] ? <Loader2 className="w-4 h-4 animate-spin"/> : <><ArrowRight className="w-4 h-4"/>Get a quote</>}
            </button>
          </div>
        ))}
      </section>

      <div className="text-xs text-ink-soft text-center">
        Insurance is provided by third-party partners. PetBond does not underwrite policies and may earn a referral commission.
        <Link to="/refund" className="ml-1 text-brand font-bold hover:underline">See our refund policy</Link>.
      </div>
    </div>
  );
}
